import * as React from "react"
import Sticky from "react-sticky-el"
import { connect } from "react-redux"

import { FieldItems, Input, ReadOnly } from "@tenforce/toolbox-field-react"
import { ItemBlockPart, Selector } from "@tenforce/toolbox-selector-react"

import { Symbols } from "@tenforce/toolbox-fontmap"
import { GlobalState } from "../../../store"
import { OldUIContext } from "../../../olduicontext"

import AbsoluteBreadcrumbs from "./absolutebreadcrumbs/AbsoluteBreadcrumbs"
import BookmarkButton from "../../bookmarks/components/BookmarkButton"
import Buttons from "./buttons/Buttons"
import OptionalHeaderButtons from "./buttons/OptionalHeaderButtons"
import ChangeView from "./ChangeView"
import CurrentList from "./absolutebreadcrumbs/CurrentList"
import PanelSwitch from "./PanelSwitch"
import ShareButton from "./buttons/ShareButton"
import Signatures from "./buttons/Signatures"
import TypeIndicator from "./TypeIndicator"
import WorkspaceCoverPageGridConfig from "../../common/grid/configuration/GridConfiguration"

import {
    getSeparatedButtonsByVisibility,
    getSortedBreadcrumbs,
    SeparatedButtons,
    SortedBreadcrumbs,
} from "../selectors"
import {
    DispatchProps as HeaderDispatchProps,
    mapDispatchToProps as headerMapDispatchToProps,
} from "../actions"
import {
    DispatchProps as CoverPageDispatchProps,
    mapDispatchToProps as coverPageMapDispatchToProps,
} from "../../workspacecoverpage/actions"

import "./Header.scss"
import { Labels } from "../../../util/Translations"
import { IGrid } from "../../../models"
import { IHeader } from "../index"

// NOTE: hardcoded to avoid extra calls to backend
const TitleMaxLength = 80

interface ItemSpecificFields {
    floatingFields: JSX.Element
    numberField: JSX.Element
    statusField: JSX.Element
}

interface StateProps extends IHeader, SortedBreadcrumbs {
    activePanel: string
    separatedButtons: SeparatedButtons
    coverPageGrid: IGrid
    isCoverPageGridLoading: boolean
}

type DispatchProps = HeaderDispatchProps & CoverPageDispatchProps

type Props = StateProps & DispatchProps

export class Header extends React.Component<Props> {
    container: React.RefObject<HTMLDivElement>
    mainHeaderContainer: React.RefObject<HTMLElement>

    constructor(props: Props) {
        super(props)
        this.container = React.createRef()
        this.mainHeaderContainer = React.createRef()
    }

    render(): JSX.Element {
        const {
            resource,
            sortedBreadcrumbs,
            itemData,
            buttonsVisibility,
            executeCallButton,
            headerTypes,
            changeViewData,
            updateSelectedListView,
            unsyncTitle,
            updateUnsyncTitle,
            showCondensed,
            separatedButtons,
            coverPageGrid,
            isCoverPageGridLoading,
        } = this.props
        const { showBookmark, bookmarkButtonId, title } = resource

        const { views, selectedView, selectionId } = changeViewData
        const showNavigation = sortedBreadcrumbs.length > 0 || !!itemData

        const { MoreActionsTooltip, RemoveBookmark, AddBookmark } = Labels
        return (
            <OldUIContext.Consumer>
                {({ openSignatures, switchToPanel }): JSX.Element => {
                    return (
                        <div ref={this.container} className="NewUI__header">
                            <Sticky
                                positionRecheckInterval={1000 / 12}
                                stickyClassName="is-stuck"
                            >
                                <header
                                    className={`NewUI-item-header ${this.getHeaderClasses()}`}
                                >
                                    {<TypeIndicator types={headerTypes} />}
                                    <div className="NewUI-item-header__wrapper bg-lightest">
                                        <section
                                            className="NewUI-item-header__main"
                                            ref={this.mainHeaderContainer}
                                        >
                                            {showNavigation && (
                                                <div
                                                    className={`NewUI-breadcrumbs tw-mt-2 ${
                                                        headerTypes.includes(
                                                            "Item",
                                                        )
                                                            ? "bg-lightest"
                                                            : ""
                                                    }`}
                                                >
                                                    {
                                                        this.getItemSpecificFields()
                                                            .numberField
                                                    }
                                                    <AbsoluteBreadcrumbs
                                                        sortedBreadcrumbs={
                                                            sortedBreadcrumbs
                                                        }
                                                        showCondensed={
                                                            showCondensed
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {!title?.hidden && (
                                                <FieldItems extraClasses="tw-mr-2">
                                                    {title.readOnly ? (
                                                        <ReadOnly
                                                            value={unsyncTitle}
                                                            extraClasses="h2"
                                                            title={unsyncTitle}
                                                        />
                                                    ) : (
                                                        <Input
                                                            maxLength={
                                                                TitleMaxLength
                                                            }
                                                            required={true}
                                                            value={unsyncTitle}
                                                            title={unsyncTitle}
                                                            onChangedValue={(
                                                                value,
                                                            ): void => {
                                                                updateUnsyncTitle(
                                                                    value,
                                                                )
                                                            }}
                                                            onKeyUp={(
                                                                event: React.KeyboardEvent<HTMLInputElement>,
                                                            ): void => {
                                                                if (
                                                                    event.key ===
                                                                    "Enter"
                                                                ) {
                                                                    event.currentTarget.blur()
                                                                }
                                                            }}
                                                            onBlur={(): void =>
                                                                this.updateTitle(
                                                                    unsyncTitle,
                                                                )
                                                            }
                                                            extraClasses="h2"
                                                        />
                                                    )}
                                                </FieldItems>
                                            )}
                                        </section>
                                        <aside className="NewUI-item-header__actions tw-pr-2">
                                            {
                                                this.getItemSpecificFields(
                                                    openSignatures,
                                                    switchToPanel,
                                                ).statusField
                                            }

                                            <Buttons
                                                buttons={
                                                    separatedButtons.visibleCustomButtons
                                                }
                                                buttonsVisibility={
                                                    buttonsVisibility
                                                }
                                                executeCallButton={
                                                    executeCallButton
                                                }
                                            />

                                            <ShareButton />
                                            {showBookmark &&
                                                !!bookmarkButtonId && (
                                                    <BookmarkButton
                                                        addBookmarkLabel={
                                                            AddBookmark
                                                        }
                                                        removeBookmarkLabel={
                                                            RemoveBookmark
                                                        }
                                                        bookmarkButtonId={
                                                            bookmarkButtonId
                                                        }
                                                    />
                                                )}

                                            <WorkspaceCoverPageGridConfig
                                                grid={coverPageGrid}
                                                isLoading={
                                                    isCoverPageGridLoading
                                                }
                                                reorderTiles={
                                                    this.onGridReorder
                                                }
                                                removeTile={
                                                    this.onGridTileRemove
                                                }
                                            />

                                            <Buttons
                                                buttons={
                                                    separatedButtons.visibleStandardButtons
                                                }
                                                buttonsVisibility={
                                                    buttonsVisibility
                                                }
                                                executeCallButton={
                                                    executeCallButton
                                                }
                                            />

                                            <OptionalHeaderButtons
                                                buttonsVisibility={
                                                    buttonsVisibility
                                                }
                                                executeCallButton={
                                                    executeCallButton
                                                }
                                                optionalCustomButtons={
                                                    separatedButtons.optionalCustomButtons
                                                }
                                                optionalStandardButtons={
                                                    separatedButtons.optionalStandardButtons
                                                }
                                                tooltip={MoreActionsTooltip}
                                            />

                                            {
                                                this.getItemSpecificFields(
                                                    openSignatures,
                                                    switchToPanel,
                                                ).floatingFields
                                            }

                                            {(views ?? []).length > 0 &&
                                                !!selectedView &&
                                                !!selectionId && (
                                                    <ChangeView
                                                        selectionId={
                                                            selectionId
                                                        }
                                                        selectedView={
                                                            selectedView
                                                        }
                                                        views={views ?? []}
                                                        updateSelectedListView={
                                                            updateSelectedListView
                                                        }
                                                    />
                                                )}
                                        </aside>
                                    </div>
                                </header>
                            </Sticky>
                        </div>
                    )
                }}
            </OldUIContext.Consumer>
        )
    }

    componentDidUpdate(prevProps: Props): void {
        if (
            prevProps.resource.title.value !== this.props.resource.title.value
        ) {
            this.setState({ unsyncTitle: this.props.resource.title.value })
        }
    }

    getItemSpecificFields = (
        openSignatures?: () => void,
        switchToPanel?: (panel: string, updateURL: boolean) => void,
    ): ItemSpecificFields => {
        const {
            PanelSwitchCommentsAttachments,
            PanelSwitchOverview,
            PanelSwitchHistory,
        } = Labels

        const {
            activePanel,
            itemData,
            resource: { statusLabel, itemType },
            sortedBreadcrumbs,
        } = this.props
        let numberField = <></>

        const statusField = (
            <>
                {itemType != "Wiki" && (statusLabel ?? "").length > 0 && (
                    <Selector
                        id="newui-resourceStatus"
                        isReadOnly={true}
                        isExpanded={false}
                        title={statusLabel}
                        isWrapped={false}
                    >
                        <ItemBlockPart
                            isSelected={true}
                            partProps={{ isLabel: true, color: "#EAEAEA" }}
                        >
                            {statusLabel}
                        </ItemBlockPart>
                    </Selector>
                )}
            </>
        )

        let floatingFields = <></>

        if (!!itemData) {
            const { actions, number, showSignatures, allSignaturesSigned } =
                itemData

            if (!!openSignatures && !!switchToPanel) {
                floatingFields = (
                    <>
                        {showSignatures && (
                            <Signatures
                                allSigned={allSignaturesSigned}
                                onOpenSignatures={openSignatures}
                            />
                        )}

                        {!!actions && (
                            <PanelSwitch
                                activePanel={activePanel}
                                onPanelSwitch={switchToPanel}
                                CommentsAttachmentsLabel={
                                    PanelSwitchCommentsAttachments ?? ""
                                }
                                HistoryLabel={PanelSwitchHistory ?? ""}
                                OverviewLabel={PanelSwitchOverview ?? ""}
                                {...actions}
                            />
                        )}
                    </>
                )
            }

            const currentList =
                sortedBreadcrumbs.length > 0
                    ? sortedBreadcrumbs[sortedBreadcrumbs.length - 1].breadcrumb
                    : null

            const showNumberField = !number?.hidden || !!currentList

            numberField = (
                <div className="NewUI-breadcrumbs__item label tw-mr-1">
                    <div className="NewUI-item-id is-inverted">
                        <span className="NewUI-item-id__shade text-black">
                            <span>{Symbols.TagFlag}</span>
                        </span>
                        {showNumberField && (
                            <span className="NewUI-item-id__nr">
                                {!!currentList && (
                                    <CurrentList currentList={currentList} />
                                )}
                                {!number?.hidden && !!currentList && (
                                    <>&nbsp;{Symbols.Dot}&nbsp;</>
                                )}
                                {!number?.hidden && (
                                    <span id="newui-itemNumber">
                                        {number?.value}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                </div>
            )
        }

        return {
            floatingFields,
            numberField,
            statusField,
        }
    }

    getHeaderClasses = (): string => {
        const { headerTypes } = this.props
        const headerClasses: Array<string> = []

        if (headerTypes?.includes("Dashboard")) {
            headerClasses.push("theme-workspace")
        }
        headerTypes?.forEach((headerType) => {
            headerClasses.push(`theme-${headerType.toLowerCase()}`)
        })
        return headerClasses.join(" ")
    }

    updateTitle = (newValue: string): void => {
        const {
            updateTitle,
            updateUnsyncTitle,
            resource: {
                title: { value: oldValue },
            },
        } = this.props

        newValue = newValue.trim()
        if (newValue === oldValue) {
            return
        }

        if (newValue.length === 0) {
            updateUnsyncTitle(oldValue)
            return
        }

        updateTitle(newValue)
    }

    onGridReorder = (
        targetIndex: number,
        oldIndex: number,
        reorderedTileId: number,
        targetTileId: number,
    ): void => {
        this.props.updateTilesOrder(
            targetIndex,
            oldIndex,
            reorderedTileId,
            targetTileId,
        )
    }

    onGridTileRemove = (tileId: number): void => {
        this.props.removeTile(tileId)
    }
}

const mapStateToProps = (state: GlobalState): StateProps => ({
    ...state.resourceHeader,
    separatedButtons: getSeparatedButtonsByVisibility(state.resourceHeader),
    coverPageGrid: state.workspaceCoverPage.grid,
    isCoverPageGridLoading: state.workspaceCoverPage.isLoading,
    sortedBreadcrumbs: getSortedBreadcrumbs(state.resourceHeader),
})

const mapDispatchToProps: DispatchProps = {
    ...headerMapDispatchToProps,
    ...coverPageMapDispatchToProps,
}

export default connect(mapStateToProps, mapDispatchToProps)(Header)
